import {
  ArrowLeft,
  ArrowRight,
  Bike,
  ClipboardList,
  Clock,
  Loader,
  Mail,
  MapPin,
  Package,
  Phone,
  Search,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './EntregasRecogidas.css';

const deliveryTypeLabel = (t) => {
  if (t === 'PICKUP') return 'Recogida en tienda';
  if (t === 'DELIVERY') return 'Entrega a domicilio';
  if (t === 'HOTEL') return 'Hotel / entrega';
  return t || '—';
};

/** Estados de preparación de la reserva (taller) */
const PREP_WORKFLOW_OPTIONS = [
  { value: null, label: 'Sin marcar' },
  { value: 'IN_PROGRESS', label: 'En preparación' },
  { value: 'READY', label: 'Preparada' },
  { value: 'FINISHED', label: 'Finalizada' },
];

/** Clases CSS para el color de la tarjeta según el estado de preparación de la reserva. */
function prepBookingCardClassName(booking) {
  const lines = booking?.lines || [];
  const allBikesDone = lines.length > 0 && lines.every((l) => l.prepPrepared);
  const s = booking?.prepWorkflowStatus;

  if (s === 'FINISHED') return 'prep-booking-card--finished';
  if (s === 'READY' || allBikesDone) return 'prep-booking-card--ready';
  if (s === 'IN_PROGRESS') return 'prep-booking-card--progress';
  return 'prep-booking-card--pending';
}

/** Normaliza texto para búsqueda: minúsculas y sin acentos. */
const normalizeSearch = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const PreparacionReservas = () => {
  const { authenticatedFetch } = useAuth();
  const navigate = useNavigate();
  const dateInputRef = useRef(null);
  const [bookings, setBookings] = useState([]);
  const [counts, setCounts] = useState({ bookings: 0, bikes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyBookingId, setBusyBookingId] = useState(null);
  const [busyRentalKey, setBusyRentalKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const fetchPrepDay = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('date', selectedDate);
      const url = `${buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/prep-day`)}?${params.toString()}`;
      const response = await authenticatedFetch(url);
      const data = await response.json();
      if (data.success) {
        setBookings(data.data?.bookings || []);
        setCounts({
          bookings: data.counts?.bookings ?? (data.data?.bookings?.length || 0),
          bikes: data.counts?.bikes ?? 0,
        });
      } else {
        setError(data.message || 'Error al cargar la preparación del día');
      }
    } catch (e) {
      console.error(e);
      setError('Error de conexión al servidor');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, selectedDate]);

  useEffect(() => {
    fetchPrepDay();
  }, [fetchPrepDay]);

  /** Filtra las reservas del día por nombre o correo electrónico del cliente. */
  const filteredBookings = useMemo(() => {
    const term = normalizeSearch(searchTerm);
    if (!term) return bookings;
    return bookings.filter(
      (b) =>
        normalizeSearch(b.contact?.name).includes(term) ||
        normalizeSearch(b.contact?.email).includes(term)
    );
  }, [bookings, searchTerm]);

  const changeDate = (days, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const currentDate = new Date(`${selectedDate}T12:00:00`);
    currentDate.setDate(currentDate.getDate() + days);
    const y = currentDate.getFullYear();
    const m = String(currentDate.getMonth() + 1).padStart(2, '0');
    const d = String(currentDate.getDate()).padStart(2, '0');
    setSelectedDate(`${y}-${m}-${d}`);
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today.toISOString().split('T')[0]);
  };

  const goToTomorrow = () => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    setSelectedDate(t.toISOString().split('T')[0]);
  };

  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* Navegador sin showPicker o input no mutable (p. ej. readOnly) */
      }
    }
    el.focus();
  };

  /**
   * Clic en el campo: abrir calendario tras el ciclo de eventos del navegador.
   * No usar readOnly en type=date: en Chromium showPicker() falla si el input es de solo lectura.
   */
  const handleDateInputClick = () => {
    const el = dateInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (typeof el.showPicker === 'function') {
        try {
          el.showPicker();
        } catch {
          el.focus();
        }
      } else {
        el.focus();
      }
    });
  };

  /** Impedir escribir la fecha con teclado; Enter/Espacio abren el calendario. */
  const handleDateInputKeyDown = (e) => {
    if (e.key === 'Tab' || e.key === 'Escape') return;
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDatePicker();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const blocksTyping =
      /^[0-9]$/.test(e.key) ||
      e.key === '/' ||
      e.key === '-' ||
      e.key === '.' ||
      e.key === 'Backspace' ||
      e.key === 'Delete';
    if (blocksTyping) {
      e.preventDefault();
    }
  };

  const formatDisplayDate = (dateString) => {
    const date = new Date(`${dateString}T12:00:00`);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dateStr = date.toDateString();
    const todayStr = today.toDateString();
    const tomorrowStr = tomorrow.toDateString();
    const yesterdayStr = yesterday.toDateString();

    if (dateStr === todayStr) return 'Hoy';
    if (dateStr === tomorrowStr) return 'Mañana';
    if (dateStr === yesterdayStr) return 'Ayer';

    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('es-ES', {
        timeZone: 'Europe/Madrid',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { label: 'Pendiente', class: 'pending' },
      CONFIRMED: { label: 'Confirmada', class: 'confirmed' },
      CANCELLED: { label: 'Cancelada', class: 'cancelled' },
      COMPLETED: { label: 'Completada', class: 'completed' },
    };
    const statusInfo = statusMap[status] || { label: status, class: 'unknown' };
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const isToday = () => {
    const today = new Date();
    const selected = new Date(`${selectedDate}T12:00:00`);
    return today.toDateString() === selected.toDateString();
  };

  const isTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const selected = new Date(`${selectedDate}T12:00:00`);
    return tomorrow.toDateString() === selected.toDateString();
  };

  const openEditBooking = (bookingId) => {
    navigate('/reservas', {
      state: {
        openEditBookingId: bookingId,
        openEditNonce: Date.now(),
      },
    });
  };

  const getGoogleMapsUrl = (address) => {
    if (!address || address === 'N/A') return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const patchBookingPrep = async (bookingId, prepWorkflowStatus) => {
    setBusyBookingId(bookingId);
    setError(null);
    try {
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${bookingId}/prep-workflow`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prepWorkflowStatus }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'No se pudo guardar el estado de preparación');
        return;
      }
      await fetchPrepDay();
    } catch (e) {
      console.error(e);
      setError('Error de conexión al guardar');
    } finally {
      setBusyBookingId(null);
    }
  };

  const patchRentalPrepared = async (bookingId, rentalId, prepared) => {
    const key = `${bookingId}-${rentalId}`;
    setBusyRentalKey(key);
    setError(null);
    try {
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${bookingId}/vehicle-rentals/${rentalId}/prep-prepared`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prepared }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'No se pudo actualizar la bicicleta');
        return;
      }
      await fetchPrepDay();
    } catch (e) {
      console.error(e);
      setError('Error de conexión al guardar');
    } finally {
      setBusyRentalKey(null);
    }
  };

  const isPrepPillActive = (booking, value) => {
    const current = booking.prepWorkflowStatus ?? null;
    return current === value;
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="loading-container">
        <Loader className="loading-spinner" />
        <p>Cargando preparación del día…</p>
      </div>
    );
  }

  return (
    <div className="entregas-recogidas-page preparacion-reservas-page">
      <div className="page-header">
        <div>
          <h1>
            <ClipboardList size={28} style={{ verticalAlign: 'middle', marginRight: 8 }} aria-hidden />
            Preparación del día
          </h1>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '0.95rem', maxWidth: 640 }}>
            Elige la fecha de inicio de alquiler. Marca el estado del pedido y cada bicicleta preparada. Los estados son
            solo para el equipo de taller.
          </p>
        </div>
      </div>

      <div className="prep-date-panel">
        <div className="prep-date-panel-header">
          <p className="prep-date-label" id="prep-date-heading">
            Fecha concreta
          </p>
          <p className="prep-date-hint">
            Usa el calendario, el campo de fecha o las flechas. La vista lista las reservas que empiezan ese día
            (hora Madrid).
          </p>
        </div>
        <div className="prep-date-controls">
          <button
            type="button"
            className="date-nav-btn date-nav-btn-prev"
            onClick={(e) => changeDate(-1, e)}
            title="Día anterior"
            aria-label="Día anterior"
          >
            <ArrowLeft size={24} strokeWidth={2.5} />
          </button>
          <input
            ref={dateInputRef}
            id="prep-day-date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            onClick={handleDateInputClick}
            onKeyDown={handleDateInputKeyDown}
            onPaste={(e) => e.preventDefault()}
            className="prep-date-input-large prep-date-input--picker-only"
            aria-labelledby="prep-date-heading"
            title="Pulse para abrir el calendario"
            autoComplete="off"
            inputMode="none"
          />
          <span className="date-label" style={{ fontWeight: 600, color: '#374151' }}>
            {formatDisplayDate(selectedDate)}
          </span>
          {!isToday() && (
            <button type="button" className="today-btn" onClick={goToToday}>
              Ir a hoy
            </button>
          )}
          {!isTomorrow() && (
            <button type="button" className="today-btn" onClick={goToTomorrow}>
              Mañana
            </button>
          )}
          <button
            type="button"
            className="date-nav-btn date-nav-btn-next"
            onClick={(e) => changeDate(1, e)}
            title="Día siguiente"
            aria-label="Día siguiente"
          >
            <ArrowRight size={24} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button type="button" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card delivery">
          <div className="stat-icon">
            <ClipboardList size={24} />
          </div>
          <div className="stat-content">
            <h3>Reservas</h3>
            <p className="stat-number">{counts.bookings}</p>
          </div>
        </div>
        <div className="stat-card pickup">
          <div className="stat-icon">
            <Bike size={24} />
          </div>
          <div className="stat-content">
            <h3>Bicicletas (líneas)</h3>
            <p className="stat-number">{counts.bikes}</p>
          </div>
        </div>
      </div>

      <div className="section-container">
        <div className="section-header delivery-header" style={{ flexWrap: 'wrap' }}>
          <Package size={24} />
          <h2>
            Lista de preparación (
            {searchTerm.trim() ? `${filteredBookings.length} de ${bookings.length}` : bookings.length})
          </h2>
          <button
            type="button"
            className="today-btn"
            style={{ marginLeft: 'auto' }}
            onClick={() => fetchPrepDay()}
            disabled={loading}
          >
            {loading ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
        {bookings.length > 0 && (
          <div className="prep-search-container">
            <Search size={16} aria-hidden />
            <input
              type="search"
              className="prep-search-input"
              placeholder="Buscar por nombre o correo electrónico…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSearchTerm('');
              }}
              aria-label="Buscar reservas por nombre o correo electrónico"
              autoComplete="off"
            />
            {searchTerm && (
              <button
                type="button"
                className="prep-search-clear"
                onClick={() => setSearchTerm('')}
                title="Limpiar búsqueda (Esc)"
                aria-label="Limpiar búsqueda"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            )}
          </div>
        )}
        {bookings.length === 0 ? (
          <div className="empty-section">
            <p>No hay reservas de bicicletas con inicio en este día.</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="empty-section">
            <p>Ninguna reserva coincide con «{searchTerm.trim()}».</p>
          </div>
        ) : (
          <div className="cards-grid">
            {filteredBookings.map((b) => (
              <div
                key={b.id}
                className={`delivery-card delivery-card-item prep-booking-card ${prepBookingCardClassName(b)}`}
              >
                <div className="card-header">
                  <div className="card-id">
                    <span className="card-id-number">#{b.id}</span>
                    {b.bookingReference && (
                      <span className="card-reference">{b.bookingReference}</span>
                    )}
                  </div>
                  {getStatusBadge(b.status)}
                </div>

                <div className="card-time">
                  <Clock size={18} />
                  <span className="time-value">{formatTimeOnly(b.startDateTime)}</span>
                  <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.85rem' }}>
                    {deliveryTypeLabel(b.deliveryType)}
                  </span>
                </div>

                <div className="prep-workflow-row">
                  <span className="prep-workflow-label">Estado preparación (reserva)</span>
                  <div className="prep-workflow-pills">
                    {PREP_WORKFLOW_OPTIONS.map((opt) => (
                      <button
                        key={opt.label}
                        type="button"
                        className={`prep-pill${isPrepPillActive(b, opt.value) ? ' is-active' : ''}`}
                        disabled={busyBookingId === b.id}
                        onClick={() => patchBookingPrep(b.id, opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="card-contact">
                  <div className="contact-item">
                    <User size={16} />
                    <strong>{b.contact.name}</strong>
                  </div>
                  <div className="contact-item">
                    <Phone size={16} />
                    <a href={`tel:${b.contact.fullPhone}`} className="phone-link">
                      {b.contact.fullPhone}
                    </a>
                  </div>
                  <div className="contact-item">
                    <Mail size={16} />
                    <span>{b.contact.email}</span>
                  </div>
                </div>

                {(b.deliveryType === 'DELIVERY' || b.deliveryType === 'HOTEL') && b.hotelAddress && (
                  <div className="card-address">
                    <MapPin size={16} />
                    {getGoogleMapsUrl(b.hotelAddress) ? (
                      <a
                        href={getGoogleMapsUrl(b.hotelAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="address-link"
                      >
                        {b.hotelAddress}
                      </a>
                    ) : (
                      <span>{b.hotelAddress}</span>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 12 }}>
                  <strong style={{ display: 'block', marginBottom: 8 }}>Bicicletas y accesorios</strong>
                  {(b.lines || []).map((line) => {
                    const rentalBusy = busyRentalKey === `${b.id}-${line.rentalId}`;
                    const prepared = !!line.prepPrepared;
                    return (
                      <div
                        key={line.rentalId}
                        className={prepared ? 'prep-line-block prep-line-prepared' : 'prep-line-block'}
                        style={{
                          borderRadius: 8,
                          padding: '10px 12px',
                          marginBottom: 8,
                          fontSize: '0.9rem',
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#111827' }}>
                          {line.model.brand} {line.model.name}
                        </div>
                        <div style={{ color: '#4b5563', marginTop: 4 }}>
                          Talla: <strong>{line.size}</strong>
                          {line.serialNumber && (
                            <>
                              {' '}
                              · N.º serie: <strong>{line.serialNumber}</strong>
                            </>
                          )}
                          {!line.serialNumber && <span> · Sin número de serie</span>}
                        </div>
                        {line.model.inseamOptionalEnabled && (
                          <div style={{ color: '#4b5563', marginTop: 4 }}>
                            Entrepierna:{' '}
                            {line.inseamCm != null && Number.isFinite(line.inseamCm) ? (
                              <strong>{line.inseamCm} cm</strong>
                            ) : (
                              <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>no indicada</span>
                            )}
                          </div>
                        )}
                        <div style={{ marginTop: 8 }}>
                          <span style={{ color: '#6b7280' }}>Accesorios: </span>
                          {line.accessories && line.accessories.length > 0 ? (
                            <span>{line.accessories.map((a) => a.name).join(', ')}</span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>Ninguno</span>
                          )}
                        </div>
                        <div className="prep-checkbox-row">
                          <input
                            id={`prep-line-${b.id}-${line.rentalId}`}
                            className="prep-line-checkbox"
                            type="checkbox"
                            checked={prepared}
                            disabled={rentalBusy}
                            onChange={(e) => patchRentalPrepared(b.id, line.rentalId, e.target.checked)}
                          />
                          <label htmlFor={`prep-line-${b.id}-${line.rentalId}`}>
                            {rentalBusy ? 'Guardando…' : 'Bicicleta preparada'}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {b.notes && (
                  <p style={{ marginTop: 10, fontSize: '0.85rem', color: '#6b7280' }}>
                    <strong>Notas:</strong> {b.notes}
                  </p>
                )}

                <div style={{ marginTop: 12 }}>
                  <button type="button" className="btn-primary" onClick={() => openEditBooking(b.id)}>
                    Editar reserva (cambiar bici / serie)
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreparacionReservas;
